import { Box, Typography, Grid, Button, Rating, Avatar, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Review, reviews, googleReviewsUrl, REVIEWS_ORIGINAL_LANGUAGE } from './reviewsData';
import Reveal from '../components/Reveal';
import { tokens } from '../theme';

type ReviewCardProps = Review & { isTranslated: boolean; translatedLabel: string };

const ReviewCard = ({ name, rating, text, date, isTranslated, translatedLabel }: ReviewCardProps) => {
  const initial = name.trim().charAt(0).toUpperCase();
  const displayText = isTranslated ? text.en : text.es;

  return (
    <Box
      component="figure"
      sx={{
        m: 0,
        p: { xs: 3, md: 3.5 },
        borderRadius: `${tokens.radius.md}px`,
        bgcolor: tokens.color.cream,
        border: '1px solid',
        borderColor: tokens.color.line,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Rating
        value={rating}
        readOnly
        precision={0.5}
        // The light brand rose sat at 1.9:1 against the cream card — below the
        // 3:1 floor for meaningful graphics. This is the same hue, readable.
        sx={{ color: tokens.color.roseInk }}
      />

      <Box sx={{ flex: 1, my: 2.5 }}>
        <Typography
          variant="body1"
          sx={{
            color: 'text.primary',
            lineHeight: 1.65,
            // Italic across a full paragraph slows reading. The quotation marks
            // already mark this as quoted speech.
            fontStyle: 'normal',
          }}
        >
          &ldquo;{displayText}&rdquo;
        </Typography>
        {isTranslated && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}
          >
            {translatedLabel}
          </Typography>
        )}
      </Box>

      <Stack component="figcaption" direction="row" alignItems="center" spacing={1.5}>
        <Avatar
          aria-hidden="true"
          sx={{
            bgcolor: tokens.color.roseInk,
            color: tokens.color.white,
            width: 40,
            height: 40,
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          {initial}
        </Avatar>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
            {name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {date}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const ReviewsSection = () => {
  const { t, i18n } = useTranslation();
  const isTranslated = !i18n.language.startsWith(REVIEWS_ORIGINAL_LANGUAGE);
  const translatedLabel = (t as any)('reviews.translatedByGoogle');

  return (
    <Box
      id="reviews-section"
      sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 2, md: 5 },
        backgroundColor: tokens.color.white,
      }}
    >
      <Reveal>
        {/* Was the hardcoded English string "Reviews" on both locales. */}
        <Typography
          variant="h2"
          component="h2"
          sx={{ textAlign: 'center', mb: { xs: 5, md: 7 }, textWrap: 'balance' }}
        >
          {(t as any)('reviews.title')}
        </Typography>
      </Reveal>

      <Grid container spacing={3}>
        {reviews.map((review, idx) => (
          <Grid key={idx} size={{ xs: 12, md: 6 }}>
            <Reveal delay={Math.min(idx, 4) * 60}>
              <ReviewCard {...review} isTranslated={isTranslated} translatedLabel={translatedLabel} />
            </Reveal>
          </Grid>
        ))}
      </Grid>

      <Reveal delay={80}>
        <Box sx={{ textAlign: 'center', mt: { xs: 5, md: 7 } }}>
          <Button
            variant="contained"
            size="large"
            href={googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
          >
            {(t as any)('reviews.seeAllOnGoogle')}
          </Button>
        </Box>
      </Reveal>
    </Box>
  );
};

export default ReviewsSection;
